import React, { useEffect, useState } from "react";
import api from "../../services/api";
import { motion } from "framer-motion";
import { 
  Users, Award, DollarSign, TrendingDown, BarChart3, 
  Loader2, TrendingUp, BookOpen, Users2
} from "lucide-react";
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, 
  BarChart, Bar, Cell
} from "recharts";

export default function AnalyticsDashboard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get("/admin/detailed-analytics")
      .then(res => setData(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  if (loading) return (
    <div className="flex h-screen items-center justify-center dark:bg-slate-900">
      <Loader2 className="animate-spin text-indigo-600" size={40} />
    </div>
  );

  if (!data) return <div className="p-10 text-center">Failed to load analytics.</div>;

  return (
    <div className="space-y-10 pb-20">
        
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-black tracking-tight text-slate-900 dark:text-white">Platform Analytics</h1>
            <p className="text-slate-500 text-sm mt-1">Global system insight, revenue vectors, and active footprint.</p>
          </div>
          <div className="px-4 py-2 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 rounded-xl text-xs font-black flex items-center gap-2 border border-emerald-200 dark:border-emerald-900/50">
             <div className="w-2 h-2 bg-emerald-500 rounded-full animate-pulse" />
             LIVE FEED
          </div>
        </div>

        {/* KPI Top Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-6">
           <KpiCard 
              icon={Users} label="Active Students" 
              value={data.active_students} 
              subtitle="+12% this month" color="bg-blue-500" 
           />
           <KpiCard 
              icon={Award} label="Course Completion" 
              value={`${data.course_completion_avg}%`} 
              subtitle="Avg user trajectory" color="bg-violet-500" 
           />
           <KpiCard 
              icon={DollarSign} label="Total Revenue" 
              value={`₹${data.total_revenue}`} 
              subtitle="Cumulative earnings" color="bg-emerald-500" 
           />
           <KpiCard 
              icon={TrendingDown} label="Drop-off Rate" 
              value={`${data.drop_off_rate}%`} 
              subtitle="Lower is better" color="bg-rose-500" 
           />
        </div>

        {/* Chart Block */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
           
           {/* Revenue Chart - Span 2 */}
           <motion.div initial={{opacity:0, y:20}} animate={{opacity:1,y:0}}
             className="lg:col-span-2 bg-white dark:bg-slate-900 p-6 rounded-3xl border dark:border-slate-800 shadow-sm"
           >
              <div className="flex items-center justify-between mb-8">
                 <h3 className="font-black flex items-center gap-2 text-slate-800 dark:text-white">
                    <BarChart3 size={18} className="text-indigo-600" /> Revenue Trajectory
                 </h3>
                 <span className="text-xs font-bold text-slate-400">Last 5 Months</span>
              </div>
              <div className="h-72 w-full">
                 <ResponsiveContainer width="100%" height="100%">
                    <AreaChart data={data.trend_data}>
                       <defs>
                          <linearGradient id="colorRev" x1="0" y1="0" x2="0" y2="1">
                             <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3}/>
                             <stop offset="95%" stopColor="#6366f1" stopOpacity={0}/>
                          </linearGradient>
                       </defs>
                       <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f1f5f9" className="dark:opacity-10" />
                       <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b'}} />
                       <YAxis axisLine={false} tickLine={false} tick={{fontSize: 12, fill: '#64748b'}} />
                       <Tooltip contentStyle={{borderRadius:'12px', border:'none', boxShadow:'0 10px 30px rgba(0,0,0,0.1)'}} />
                       <Area type="monotone" dataKey="revenue" stroke="#6366f1" strokeWidth={3} fillOpacity={1} fill="url(#colorRev)" />
                    </AreaChart>
                 </ResponsiveContainer>
              </div>
           </motion.div>

           {/* Student signup simple growth chart */}
           <motion.div initial={{opacity:0, y:20}} animate={{opacity:1,y:0}}
             className="bg-gradient-to-br from-indigo-600 to-blue-700 p-8 rounded-3xl text-white shadow-xl flex flex-col"
           >
              <div className="flex items-center justify-between">
                 <Users2 size={32} className="opacity-60" />
                 <span className="text-xs font-black px-3 py-1 bg-white/20 rounded-full backdrop-blur-sm">SIGNS-UP TREND</span>
              </div>
              <div className="flex-1 flex flex-col justify-center mt-6">
                 <h2 className="text-5xl font-black tracking-tighter flex items-baseline gap-2">
                    {data.active_students}
                    <TrendingUp size={28} className="text-emerald-300 animate-bounce" />
                 </h2>
                 <p className="text-indigo-100 font-medium mt-2">Total user conversions recorded so far.</p>
              </div>
              <div className="h-24 w-full mt-6 opacity-60">
                 <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={data.trend_data}>
                       <Bar dataKey="signups" radius={[4,4,0,0]}>
                          {data.trend_data.map((entry, i) => (
                             <Cell key={i} fill="#ffffff" />
                          ))}
                       </Bar>
                    </BarChart>
                 </ResponsiveContainer>
              </div>
           </motion.div>
        </div>

        {/* Teacher Performance Leaderboard */}
        <motion.div initial={{opacity:0,y:20}} animate={{opacity:1,y:0}}
           className="bg-white dark:bg-slate-900 p-8 rounded-[2.5rem] border dark:border-slate-800 shadow-sm"
        >
           <h3 className="text-xl font-black text-slate-900 dark:text-white mb-6 flex items-center gap-3">
              <BookOpen size={22} className="text-amber-500" /> Teacher Performance Index
           </h3>
           <div className="overflow-x-auto">
              <table className="w-full text-sm">
                 <thead>
                    <tr className="text-left border-b dark:border-slate-800">
                       <th className="pb-4 font-black text-slate-400 uppercase text-[10px] tracking-widest">Educator</th>
                       <th className="pb-4 font-black text-slate-400 uppercase text-[10px] tracking-widest text-center">Courses</th>
                       <th className="pb-4 font-black text-slate-400 uppercase text-[10px] tracking-widest text-center">Lectures</th>
                       <th className="pb-4 font-black text-slate-400 uppercase text-[10px] tracking-widest text-center">Live</th>
                       <th className="pb-4 font-black text-slate-400 uppercase text-[10px] tracking-widest text-center">Quizzes</th>
                       <th className="pb-4 font-black text-slate-400 uppercase text-[10px] tracking-widest text-center">Doubts</th>
                       <th className="pb-4 font-black text-slate-400 uppercase text-[10px] tracking-widest text-center">Assn</th>
                       <th className="pb-4 font-black text-slate-400 uppercase text-[10px] tracking-widest text-center">Students</th>
                       <th className="pb-4 font-black text-slate-400 uppercase text-[10px] tracking-widest text-right">Platform Score</th>
                    </tr>
                 </thead>
                 <tbody>
                    {data.teacher_leaderboard.length > 0 ? data.teacher_leaderboard.map((teach, i) => (
                       <tr key={i} className="border-b dark:border-slate-800/50 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition-colors">
                          <td className="py-5 flex items-center gap-3">
                             <div className="w-9 h-9 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center font-black text-slate-500 shadow-sm border border-slate-200 dark:border-slate-700">
                                {teach.name[0].toUpperCase()}
                             </div>
                             <span className="font-extrabold text-slate-900 dark:text-white">{teach.name}</span>
                          </td>
                          <td className="py-5 text-center font-black text-slate-700 dark:text-slate-300">{teach.courses_published}</td>
                          <td className="py-5 text-center font-medium text-slate-600 dark:text-slate-400">{teach.lectures_count || 0}</td>
                          <td className="py-5 text-center font-medium text-slate-600 dark:text-slate-400">{teach.live_count || 0}</td>
                          <td className="py-5 text-center font-medium text-slate-600 dark:text-slate-400">{teach.exams_count || 0}</td>
                          <td className="py-5 text-center font-medium text-slate-600 dark:text-slate-400">{teach.doubts_count || 0}</td>
                          <td className="py-5 text-center font-medium text-slate-600 dark:text-slate-400">{teach.assignments_count || 0}</td>
                          <td className="py-5 text-center font-black text-indigo-600 dark:text-indigo-400">{teach.students_enrolled}</td>
                          <td className="py-5 text-right">
                             <div className="inline-flex items-center px-3 py-1 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 rounded-lg font-black text-xs shadow-sm">
                                {teach.score} / 100
                             </div>
                          </td>
                       </tr>
                    )) : (
                       <tr><td colSpan={9} className="py-10 text-center text-slate-400 italic">No ranking data available yet.</td></tr>
                    )}
                 </tbody>
              </table>
           </div>
        </motion.div>

    </div>
  );
}

function KpiCard({ icon: Icon, label, value, subtitle, color }) {
   return (
      <motion.div whileHover={{y:-4}} className="bg-white dark:bg-slate-900 p-6 rounded-3xl border dark:border-slate-800 shadow-sm flex gap-5 items-center">
         <div className={`w-14 h-14 rounded-2xl ${color} text-white flex items-center justify-center shadow-lg shrink-0`}>
            <Icon size={24} />
         </div>
         <div>
            <p className="text-[11px] font-black uppercase text-slate-400 tracking-widest">{label}</p>
            <p className="text-2xl font-black text-slate-900 dark:text-white mt-0.5">{value}</p>
            <p className="text-xs font-medium text-slate-500 mt-1">{subtitle}</p>
         </div>
      </motion.div>
   );
}
